/**
 * Build flavours of the mobile app (BR-14).
 *
 * Pure module — no `import.meta`, no React — so it can run under `node --test`.
 * `suite` is the third app that contains both single apps; its store name is the
 * company name "Foroba" (the container shows as Kelen). `naforo` and `yiriba` are the two single apps.
 */
export type Flavor = 'suite' | 'naforo' | 'yiriba'

export const FLAVORS: readonly Flavor[] = ['suite', 'naforo', 'yiriba']

export interface FlavorInfo {
  id: Flavor
  /** Home-screen / store display name. */
  appName: string
  /** iOS bundle identifier. Not user-visible; stable once published. */
  bundleId: string
  /** Value for `data-app` on <html> → picks the per-app accent from @foroba/ui tokens. */
  dataApp: 'kelen' | 'naforo' | 'yiriba'
  /** Brand accent for the browser/PWA chrome (`theme-color`, manifest). Matches the foroba-ui token. */
  themeColor: string
  /** Which embedded apps are reachable in this flavour. */
  hasNaforo: boolean
  hasYiriba: boolean
}

export const FLAVOR_INFO: Record<Flavor, FlavorInfo> = {
  suite: { id: 'suite', appName: 'Kelen', bundleId: 'app.foroba.pfh', dataApp: 'kelen', themeColor: '#2F5BEA', hasNaforo: true, hasYiriba: true },
  naforo: { id: 'naforo', appName: 'Naforo', bundleId: 'app.foroba.naforo', dataApp: 'naforo', themeColor: '#B8892A', hasNaforo: true, hasYiriba: false },
  yiriba: { id: 'yiriba', appName: 'Yiriba', bundleId: 'app.foroba.yiriba', dataApp: 'yiriba', themeColor: '#1A7F52', hasNaforo: false, hasYiriba: true },
}

/** Unknown, empty or hostile values fall back to the container app. */
export function parseFlavor(raw: unknown): Flavor {
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : ''
  return (FLAVORS as readonly string[]).includes(v) ? (v as Flavor) : 'suite'
}

// Route prefixes owned by each embedded app (mirrors routeTree.tsx).
export const NAFORO_PATHS: readonly string[] = [
  '/accounts', '/transactions', '/recurring', '/spending', '/forecasts',
  '/budgets', '/goals', '/debts', '/pazuru/lab', '/pazuru/import-log', '/import',
]
export const YIRIBA_PATHS: readonly string[] = [
  '/portfolio', '/etf', '/crypto', '/asset/', '/analysis', '/backtest',
  '/screener', '/compare', '/sentiment', '/tax', '/simulate', '/markets', '/tsumi/lab',
]

const under = (path: string, prefixes: readonly string[]) =>
  prefixes.some((p) => path === p || path.startsWith(p.endsWith('/') ? p : `${p}/`))

/** True when `path` belongs to an app that this flavour does not ship. */
export function isPathHidden(flavor: Flavor, path: string): boolean {
  const f = FLAVOR_INFO[flavor]
  if (!f.hasNaforo && under(path, NAFORO_PATHS)) return true
  if (!f.hasYiriba && under(path, YIRIBA_PATHS)) return true
  return false
}
